/**
 * Computes comprehensive session analytics from raw telemetry events.
 * Extracted from ReplayEventLog's observables computation.
 * 
 * Usage:
 *   const { full, before, after } = computeSessionStats(events)
 *   
 * For per-window analysis (e.g. per-query slices in research dataset):
 *   import { calculateStats } from './computeSessionStats'
 *   const windowStats = calculateStats(eventsInWindow)
 */

export function calculateStats(eventList) {
  if (!eventList || eventList.length === 0) {
    return {
      regionTimes: { CODE_EDITOR: 0, CHAT_WINDOW: 0, TERMINAL: 0, TASK_DESCRIPTION: 0, TEST_CASES: 0, BACKGROUND: 0 },
      timeSinceLast: { codeEdit: null, terminalRun: null, error: null, chat: null },
      idleStats: { latestIdle: null, longestIdle: null },
      frequency: {
        codeEdit: { count: 0, perMin: '0.0' }, codeSelect: { count: 0, perMin: '0.0' },
        codeCopy: { count: 0, perMin: '0.0' }, codePaste: { count: 0, perMin: '0.0' },
        codeDelete: { count: 0, perMin: '0.0' }, codeUndo: { count: 0, perMin: '0.0' },
        terminalRun: { count: 0, perMin: '0.0' }, terminalError: { count: 0, perMin: '0.0' },
        terminalOutput: { count: 0, perMin: '0.0' }, terminalSelect: { count: 0, perMin: '0.0' },
        terminalCopy: { count: 0, perMin: '0.0' },
        chatInputType: { count: 0, perMin: '0.0' }, chatInputSelect: { count: 0, perMin: '0.0' },
        chatInputCopy: { count: 0, perMin: '0.0' }, chatInputPaste: { count: 0, perMin: '0.0' },
        chatQuery: { count: 0, perMin: '0.0' }, chatHistorySelect: { count: 0, perMin: '0.0' },
        chatHistoryCopy: { count: 0, perMin: '0.0' },
        mouseMove: { count: 0, perMin: '0.0' }, mouseClick: { count: 0, perMin: '0.0' },
      },
      terminalMetrics: { meanTimeBetweenRuns: null, errorReadingTime: null, errorToEditLatency: null, errorToQueryLatency: null, errorToResolution: null, errorCount: 0, selfFixCount: 0, aiFixCount: 0 },
      failedTestMetrics: { failedTestReadingTime: null, failedTestToEditLatency: null, failedTestToQueryLatency: null, failedTestToResolution: null, failedTestCount: 0, selfFixCount: 0, aiFixCount: 0 },
      codeMetrics: { netCodeGrowth: 0, deletionToTypeRatio: null, totalInserted: 0, totalDeleted: 0 },
      chatMetrics: { timeToHelp: null, chatToCodeLatency: null, avgQueryLength: null, responseReadingTime: null, queryCadence: { avg: null, min: null, max: null }, queryLengthTrend: { firstHalf: null, secondHalf: null, direction: null } },
      tabVisibility: { hiddenTime: 0, hiddenCount: 0 },
      taskTestInteractions: { taskSelect: 0, taskCopy: 0, testSelect: 0, testCopy: 0 },
      testResults: { passCount: 0, failCount: 0, lastPassedCount: null, lastTotalTests: null },
      errorPatterns: { consecutiveErrors: 0, maxConsecutiveErrors: 0 },
      periodDuration: 0,
    }
  }

  const periodStart = eventList[0].timestamp
  const periodEnd = eventList[eventList.length - 1].timestamp
  const periodDuration = periodEnd - periodStart

  // Region times
  const regionTimes = { CODE_EDITOR: 0, CHAT_WINDOW: 0, TERMINAL: 0, TASK_DESCRIPTION: 0, TEST_CASES: 0, BACKGROUND: 0 }
  const mouseEvents = eventList.filter(e => (e.type === 'MOUSE_MOVE' || e.type === 'MOUSE_CLICK') && e.payload?.region)
  for (let i = 0; i < mouseEvents.length - 1; i++) {
    const current = mouseEvents[i]
    const next = mouseEvents[i + 1]
    const region = current.payload.region
    const dur = next.timestamp - current.timestamp
    if (dur > 0 && dur < 30000 && regionTimes.hasOwnProperty(region)) {
      regionTimes[region] += dur
    }
  }

  // Time since last
  const lastCodeEdit = [...eventList].reverse().find(e => e.type?.startsWith('CODE_'))
  const lastTerminalRun = [...eventList].reverse().find(e => e.type === 'TERMINAL_RUN')
  const lastError = [...eventList].reverse().find(e => e.type === 'TERMINAL_ERROR' || e.type === 'CHAT_ERROR')
  const lastChat = [...eventList].reverse().find(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
  const timeSinceLast = {
    codeEdit: lastCodeEdit ? periodEnd - lastCodeEdit.timestamp : null,
    terminalRun: lastTerminalRun ? periodEnd - lastTerminalRun.timestamp : null,
    error: lastError ? periodEnd - lastError.timestamp : null,
    chat: lastChat ? periodEnd - lastChat.timestamp : null,
  }

  // Idle stats
  const IDLE_THRESHOLD = 2000
  let latestIdle = null, longestIdle = null
  for (let i = 0; i < eventList.length - 1; i++) {
    const gap = eventList[i + 1].timestamp - eventList[i].timestamp
    if (gap >= IDLE_THRESHOLD) {
      latestIdle = gap
      if (longestIdle === null || gap > longestIdle) longestIdle = gap
    }
  }

  // Frequency counts
  const count = (fn) => eventList.filter(fn).length
  const codeEditCount = count(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')
  const codeSelectCount = count(e => e.type === 'CODE_SELECT')
  const codeCopyCount = count(e => e.type === 'CODE_COPY')
  const codePasteCount = count(e => e.type === 'CODE_PASTE')
  const codeDeleteCount = count(e => e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION' || e.type === 'CODE_CUT')
  const codeUndoCount = count(e => e.type === 'CODE_UNDO' || e.type === 'CODE_REDO')
  const terminalRunCount = count(e => e.type === 'TERMINAL_RUN')
  const terminalErrorCount = count(e => e.type === 'TERMINAL_ERROR')
  const terminalOutputCount = count(e => e.type === 'TERMINAL_OUTPUT')
  const terminalSelectCount = count(e => e.type === 'TERMINAL_SELECT')
  const terminalCopyCount = count(e => e.type === 'TERMINAL_COPY')
  const chatTypeCount = count(e => e.type === 'CHAT_TYPE')
  const chatInputSelectCount = count(e => e.type === 'CHAT_SELECT_INPUT')
  const chatInputCopyCount = count(e => e.type === 'CHAT_COPY_INPUT')
  const chatInputPasteCount = count(e => e.type === 'CHAT_PASTE')
  const chatQueryCount = count(e => e.type === 'CHAT_QUERY')
  const chatHistorySelectCount = count(e => e.type === 'CHAT_SELECT_HISTORY')
  const chatHistoryCopyCount = count(e => e.type === 'CHAT_COPY_HISTORY')
  const mouseMoveCount = count(e => e.type === 'MOUSE_MOVE')
  const mouseClickCount = count(e => e.type === 'MOUSE_CLICK')

  const minutes = periodDuration / 60000 || 1
  const rate = (c) => ({ count: c, perMin: (c / minutes).toFixed(1) })
  const frequency = {
    codeEdit: rate(codeEditCount), codeSelect: rate(codeSelectCount),
    codeCopy: rate(codeCopyCount), codePaste: rate(codePasteCount),
    codeDelete: rate(codeDeleteCount), codeUndo: rate(codeUndoCount),
    terminalRun: rate(terminalRunCount), terminalError: rate(terminalErrorCount),
    terminalOutput: rate(terminalOutputCount), terminalSelect: rate(terminalSelectCount),
    terminalCopy: rate(terminalCopyCount),
    chatInputType: rate(chatTypeCount), chatInputSelect: rate(chatInputSelectCount),
    chatInputCopy: rate(chatInputCopyCount), chatInputPaste: rate(chatInputPasteCount),
    chatQuery: rate(chatQueryCount), chatHistorySelect: rate(chatHistorySelectCount),
    chatHistoryCopy: rate(chatHistoryCopyCount),
    mouseMove: rate(mouseMoveCount), mouseClick: rate(mouseClickCount),
  }

  // Terminal metrics
  const terminalRuns = eventList.filter(e => e.type === 'TERMINAL_RUN')
  let meanTimeBetweenRuns = null
  if (terminalRuns.length > 1) {
    let totalGap = 0
    for (let i = 1; i < terminalRuns.length; i++) totalGap += terminalRuns[i].timestamp - terminalRuns[i - 1].timestamp
    meanTimeBetweenRuns = totalGap / (terminalRuns.length - 1)
  }

  const terminalErrors = eventList.filter(e => e.type === 'TERMINAL_ERROR')

  // Helper: compute avg latency from a set of source events to a matching next event
  const avgLatency = (sources, matchFn) => {
    const latencies = []
    for (const src of sources) {
      const next = eventList.find(e => e.timestamp > src.timestamp && matchFn(e))
      if (next) latencies.push(next.timestamp - src.timestamp)
    }
    return latencies.length > 0 ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null
  }

  // Helper: resolution = next TEST_CASE_RESULT where passed_count > this event's baseline
  const avgResolutionLatency = (sources, getBaseline) => {
    const latencies = []
    for (const src of sources) {
      const baseline = getBaseline(src)
      const next = eventList.find(e =>
        e.timestamp > src.timestamp &&
        e.type === 'TEST_CASE_RESULT' &&
        (e.payload?.passed_count || 0) > baseline
      )
      if (next) latencies.push(next.timestamp - src.timestamp)
    }
    return latencies.length > 0 ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null
  }

  // Terminal error metrics (all 4)
  const errorReadingTime = avgLatency(terminalErrors, e => e.type?.startsWith('CODE_') || e.type === 'CHAT_QUERY' || e.type === 'TERMINAL_RUN')
  const errorToEditLatency = avgLatency(terminalErrors, e => e.type?.startsWith('CODE_'))
  const errorToQueryLatency = avgLatency(terminalErrors, e => e.type === 'CHAT_QUERY')
  const errorToResolution = avgResolutionLatency(terminalErrors, (src) => {
    const prev = [...eventList].filter(e => e.timestamp <= src.timestamp && e.type === 'TEST_CASE_RESULT').pop()
    return prev?.payload?.passed_count || 0
  })

  // Failed test metrics (all 4)
  const failedTests = eventList.filter(e => {
    if (e.type !== 'TEST_CASE_RESULT') return false
    const { passed_count, total_tests } = e.payload || {}
    return total_tests > 0 && passed_count < total_tests
  })

  const failedTestReadingTime = avgLatency(failedTests, e => e.type?.startsWith('CODE_') || e.type === 'CHAT_QUERY' || e.type === 'TERMINAL_RUN')
  const failedTestToEditLatency = avgLatency(failedTests, e => e.type?.startsWith('CODE_'))
  const failedTestToQueryLatency = avgLatency(failedTests, e => e.type === 'CHAT_QUERY')
  const failedTestToResolution = avgResolutionLatency(failedTests, (src) => src.payload?.passed_count || 0)

  // Self-fix vs AI-fix
  const classifyResponse = (sources) => {
    let selfFix = 0, aiFix = 0
    for (const src of sources) {
      const next = eventList.find(e => e.timestamp > src.timestamp && (e.type?.startsWith('CODE_') || e.type === 'CHAT_QUERY'))
      if (next) {
        if (next.type === 'CHAT_QUERY') aiFix++
        else selfFix++
      }
    }
    return { selfFixCount: selfFix, aiFixCount: aiFix }
  }
  const errorFixPattern = classifyResponse(terminalErrors)
  const failedTestFixPattern = classifyResponse(failedTests)

  // Code metrics
  let netCodeGrowth = 0, totalInserted = 0, totalDeleted = 0
  for (const event of eventList.filter(e => e.type?.startsWith('CODE_'))) {
    if (event.payload?.changes) {
      for (const change of event.payload.changes) {
        const inserted = change.text?.length || 0
        const deleted = (change.to || 0) - (change.from || 0)
        totalInserted += inserted
        totalDeleted += deleted
        netCodeGrowth += inserted - deleted
      }
    }
  }
  const typeEvents = count(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')
  const deleteEvents = count(e => e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION' || e.type === 'CODE_CUT')
  const deletionToTypeRatio = typeEvents > 0 ? (deleteEvents / typeEvents).toFixed(2) : null

  // Chat metrics
  const timeToHelpValues = []
  for (const error of terminalErrors) {
    const next = eventList.find(e => e.timestamp > error.timestamp && e.type === 'CHAT_QUERY')
    if (next) timeToHelpValues.push(next.timestamp - error.timestamp)
  }
  const timeToHelp = timeToHelpValues.length > 0
    ? timeToHelpValues.reduce((a, b) => a + b, 0) / timeToHelpValues.length : null

  const chatToCodeLatencies = []
  for (const response of eventList.filter(e => e.type === 'CHAT_RESPONSE')) {
    const next = eventList.find(e => e.timestamp > response.timestamp && e.type?.startsWith('CODE_'))
    if (next) chatToCodeLatencies.push(next.timestamp - response.timestamp)
  }
  const chatToCodeLatency = chatToCodeLatencies.length > 0
    ? chatToCodeLatencies.reduce((a, b) => a + b, 0) / chatToCodeLatencies.length : null

  const chatQueries = eventList.filter(e => e.type === 'CHAT_QUERY')
  let avgQueryLength = null
  if (chatQueries.length > 0) {
    const totalLength = chatQueries.reduce((sum, q) => {
      const text = q.payload?.text || q.payload?.message || q.payload?.content || ''
      return sum + text.length
    }, 0)
    avgQueryLength = Math.round(totalLength / chatQueries.length)
  }

  // AI response reading time
  const responseReadingLatencies = []
  for (const response of eventList.filter(e => e.type === 'CHAT_RESPONSE')) {
    const next = eventList.find(e => e.timestamp > response.timestamp && e.type !== 'MOUSE_MOVE' && e.type !== 'TAB_STATE')
    if (next) responseReadingLatencies.push(next.timestamp - response.timestamp)
  }
  const responseReadingTime = responseReadingLatencies.length > 0
    ? responseReadingLatencies.reduce((a, b) => a + b, 0) / responseReadingLatencies.length : null

  // Query cadence
  let queryCadence = { avg: null, min: null, max: null }
  if (chatQueries.length > 1) {
    const gaps = []
    for (let i = 1; i < chatQueries.length; i++) {
      gaps.push(chatQueries[i].timestamp - chatQueries[i - 1].timestamp)
    }
    queryCadence = {
      avg: gaps.reduce((a, b) => a + b, 0) / gaps.length,
      min: Math.min(...gaps),
      max: Math.max(...gaps),
    }
  }

  // Query length trend
  let queryLengthTrend = { firstHalf: null, secondHalf: null, direction: null }
  if (chatQueries.length >= 2) {
    const lengths = chatQueries.map(q => (q.payload?.text || q.payload?.message || q.payload?.content || '').length)
    const mid = Math.ceil(lengths.length / 2)
    const firstHalf = lengths.slice(0, mid)
    const secondHalf = lengths.slice(mid)
    const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length
    const avgSecond = secondHalf.length > 0 ? secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length : avgFirst
    const diff = avgSecond - avgFirst
    queryLengthTrend = {
      firstHalf: Math.round(avgFirst),
      secondHalf: Math.round(avgSecond),
      direction: Math.abs(diff) < 5 ? 'stable' : diff > 0 ? 'increasing' : 'decreasing',
    }
  }

  // Tab visibility
  const tabEvents = eventList.filter(e => e.type === 'TAB_STATE')
  let hiddenTime = 0, hiddenCount = 0, lastHiddenTimestamp = null
  for (const event of tabEvents) {
    if (event.payload?.visible === false) { lastHiddenTimestamp = event.timestamp; hiddenCount++ }
    else if (event.payload?.visible === true && lastHiddenTimestamp !== null) {
      hiddenTime += event.timestamp - lastHiddenTimestamp; lastHiddenTimestamp = null
    }
  }
  if (lastHiddenTimestamp !== null) hiddenTime += periodEnd - lastHiddenTimestamp

  // Task/test interactions
  const taskTestInteractions = {
    taskSelect: count(e => e.type === 'TASK_SELECT'),
    taskCopy: count(e => e.type === 'TASK_COPY'),
    testSelect: count(e => e.type === 'TEST_SELECT'),
    testCopy: count(e => e.type === 'TEST_COPY'),
  }

  // Test results
  const testResultEvents = eventList.filter(e => e.type === 'TEST_CASE_RESULT')
  let passCount = 0, failCount = 0, lastPassedCount = null, lastTotalTests = null
  for (const event of testResultEvents) {
    const passed = event.payload?.passed_count || 0
    const total = event.payload?.total_tests || 0
    if (passed > 0) passCount++
    if (total - passed > 0) failCount++
    lastPassedCount = passed
    lastTotalTests = total
  }

  // Error patterns
  let currentConsecutive = 0, maxConsecutiveErrors = 0, lastTestPassed = 0
  for (const event of eventList) {
    if (event.type === 'TERMINAL_ERROR') {
      currentConsecutive++
      maxConsecutiveErrors = Math.max(maxConsecutiveErrors, currentConsecutive)
    } else if (event.type === 'TEST_CASE_RESULT') {
      const passed = event.payload?.passed_count || 0
      if (passed > lastTestPassed) { currentConsecutive = 0; lastTestPassed = passed }
    } else if (event.type?.startsWith('CODE_') && event.payload?.changes) {
      let netChange = 0
      for (const change of event.payload.changes) {
        netChange += (change.text?.length || 0) - ((change.to || 0) - (change.from || 0))
      }
      if (netChange > 5) currentConsecutive = 0
    }
  }

  return {
    regionTimes, timeSinceLast,
    idleStats: { latestIdle, longestIdle },
    frequency,
    terminalMetrics: { meanTimeBetweenRuns, errorReadingTime, errorToEditLatency, errorToQueryLatency, errorToResolution, errorCount: terminalErrors.length, ...errorFixPattern },
    failedTestMetrics: { failedTestReadingTime, failedTestToEditLatency, failedTestToQueryLatency, failedTestToResolution, failedTestCount: failedTests.length, ...failedTestFixPattern },
    codeMetrics: { netCodeGrowth, deletionToTypeRatio, totalInserted, totalDeleted },
    chatMetrics: { timeToHelp, chatToCodeLatency, avgQueryLength, responseReadingTime, queryCadence, queryLengthTrend },
    tabVisibility: { hiddenTime, hiddenCount },
    taskTestInteractions,
    testResults: { passCount, failCount, lastPassedCount, lastTotalTests },
    errorPatterns: { consecutiveErrors: currentConsecutive, maxConsecutiveErrors },
    periodDuration,
  }
}

/**
 * Compute stats for full session, before first query, and after first query.
 */
export function computeSessionStats(events) {
  if (!events || events.length === 0) {
    const empty = calculateStats([])
    return { full: empty, before: empty, after: empty, aiProgressionTimeline: [] }
  }

  const firstQueryIndex = events.findIndex(e => e.type === 'CHAT_QUERY')
  const beforeEvents = firstQueryIndex === -1 ? events : events.slice(0, firstQueryIndex)
  const afterEvents = firstQueryIndex === -1 ? [] : events.slice(firstQueryIndex)

  const aiProgressionTimeline = []
  const queryIndices = []
  events.forEach((e, i) => { if (e.type === 'CHAT_QUERY') queryIndices.push(i) })

  const computeSegmentStats = (segEvents) => {
    const testResults = segEvents.filter(e => e.type === 'TEST_CASE_RESULT')
    const lastTest = testResults.length > 0 ? testResults[testResults.length - 1] : null
    const errors = segEvents.filter(e => e.type === 'TERMINAL_ERROR').length
    let codeGrowth = 0
    for (const ev of segEvents) {
      if (ev.type?.startsWith('CODE_') && ev.payload?.changes) {
        for (const c of ev.payload.changes) {
          codeGrowth += (c.text?.length || 0) - ((c.to || 0) - (c.from || 0))
        }
      }
    }
    return { testPassed: lastTest?.payload?.passed_count ?? null, testTotal: lastTest?.payload?.total_tests ?? null, errorCount: errors, netCodeGrowth: codeGrowth }
  }

  if (queryIndices.length > 0) {
    const preQueryEvents = events.slice(0, queryIndices[0])
    const preStats = computeSegmentStats(preQueryEvents)
    aiProgressionTimeline.push({ type: 'segment', label: 'Before AI', ...preStats, duration: queryIndices[0] > 0 ? events[queryIndices[0]].timestamp - events[0].timestamp : 0 })

    let prevSegStats = preStats
    for (let q = 0; q < queryIndices.length; q++) {
      const queryEvent = events[queryIndices[q]]
      const queryText = queryEvent.payload?.text || queryEvent.payload?.message || queryEvent.payload?.content || ''
      aiProgressionTimeline.push({ type: 'query', label: `Query ${q + 1}`, text: queryText.length > 120 ? queryText.slice(0, 120) + '...' : queryText, timestamp: queryEvent.timestamp })

      const segStart = queryIndices[q] + 1
      const segEnd = q < queryIndices.length - 1 ? queryIndices[q + 1] : events.length
      const segEvents = events.slice(segStart, segEnd)
      const segStats = computeSegmentStats(segEvents)
      const testDelta = (segStats.testPassed ?? 0) - (prevSegStats.testPassed ?? 0)
      const errorDelta = segStats.errorCount - prevSegStats.errorCount
      aiProgressionTimeline.push({ type: 'segment', label: q < queryIndices.length - 1 ? `Between Q${q + 1} → Q${q + 2}` : `After Q${q + 1}`, ...segStats, testDelta, errorDelta, duration: segEvents.length > 0 ? segEvents[segEvents.length - 1].timestamp - events[queryIndices[q]].timestamp : 0 })
      prevSegStats = segStats
    }
  }

  return { full: calculateStats(events), before: calculateStats(beforeEvents), after: calculateStats(afterEvents), aiProgressionTimeline }
}

// ─── Derived Behavior ──────────────────────────────────────────────────────────

function deriveBehavior(scopeStats, scopeEvents) {
  const s = scopeStats
  const dur = s.periodDuration / 1000 || 1
  let totalIdleTime = 0
  for (let i = 0; i < scopeEvents.length - 1; i++) {
    const gap = scopeEvents[i + 1].timestamp - scopeEvents[i].timestamp
    if (gap >= 2000) totalIdleTime += gap
  }
  const msToSec = (ms) => ms != null ? Math.round(ms / 1000) : 0
  const idleTimeSec = Math.round(totalIdleTime / 1000)
  const activeTimeSec = Math.max(0, Math.round(dur) - idleTimeSec)

  return {
    totalEdits: s.frequency.codeEdit.count, totalRuns: s.frequency.terminalRun.count, totalErrors: s.frequency.terminalError.count,
    copyPasteCount: s.frequency.codeCopy.count + s.frequency.codePaste.count, tabSwitches: s.tabVisibility.hiddenCount, focusLossCount: s.tabVisibility.hiddenCount,
    keystrokeRate: dur > 0 ? Math.round((s.frequency.codeEdit.count / dur) * 60) : 0, idleTime: idleTimeSec, longestIdle: msToSec(s.idleStats.longestIdle),
    netCodeGrowth: s.codeMetrics.netCodeGrowth, totalInserted: s.codeMetrics.totalInserted, totalDeleted: s.codeMetrics.totalDeleted,
    deleteTypeRatio: s.codeMetrics.deletionToTypeRatio !== null ? parseFloat(s.codeMetrics.deletionToTypeRatio) : 0,
    meanTimeBetweenRuns: msToSec(s.terminalMetrics.meanTimeBetweenRuns), consecutiveErrors: s.errorPatterns.maxConsecutiveErrors,
    aiQueries: s.frequency.chatQuery.count, activeTime: activeTimeSec,
    timeByRegion: { codeEditor: Math.round((s.regionTimes.CODE_EDITOR || 0) / 1000), chat: Math.round((s.regionTimes.CHAT_WINDOW || 0) / 1000), terminal: Math.round((s.regionTimes.TERMINAL || 0) / 1000), taskDescription: Math.round((s.regionTimes.TASK_DESCRIPTION || 0) / 1000), testPanel: Math.round((s.regionTimes.TEST_CASES || 0) / 1000) },
    errorReadingTime: msToSec(s.terminalMetrics.errorReadingTime), errorToEditLatency: msToSec(s.terminalMetrics.errorToEditLatency), errorToQueryLatency: msToSec(s.terminalMetrics.errorToQueryLatency), errorToResolution: msToSec(s.terminalMetrics.errorToResolution),
    errorCount: s.terminalMetrics.errorCount, errorSelfFixCount: s.terminalMetrics.selfFixCount, errorAiFixCount: s.terminalMetrics.aiFixCount,
    failedTestReadingTime: msToSec(s.failedTestMetrics.failedTestReadingTime), failedTestToEditLatency: msToSec(s.failedTestMetrics.failedTestToEditLatency), failedTestToQueryLatency: msToSec(s.failedTestMetrics.failedTestToQueryLatency), failedTestToResolution: msToSec(s.failedTestMetrics.failedTestToResolution),
    failedTestCount: s.failedTestMetrics.failedTestCount, failedTestSelfFixCount: s.failedTestMetrics.selfFixCount, failedTestAiFixCount: s.failedTestMetrics.aiFixCount,
    timeToHelp: msToSec(s.chatMetrics.timeToHelp), chatToCodeLatency: msToSec(s.chatMetrics.chatToCodeLatency), avgQueryLengthChars: s.chatMetrics.avgQueryLength,
    testResults: s.testResults, taskTestInteractions: s.taskTestInteractions,
  }
}

// ─── Unified Student Analytics ──────────────────────────────────────────────────

export function computeStudentAnalytics({ rawEvents, conversation, autoSegmentEvents }) {
  const conv = conversation || []
  const msToSec = (ms) => ms != null ? Math.round(ms / 1000) : 0

  let filteredEvents = [], replayStartTime = 0, replayDuration = 0
  if (rawEvents && rawEvents.length > 0) {
    const start = rawEvents[0].timestamp
    const filtered = rawEvents
    const end = filtered[filtered.length - 1].timestamp
    replayStartTime = start; replayDuration = end - start; filteredEvents = filtered
  }

  const sessionStats = computeSessionStats(filteredEvents)
  const firstQueryEvent = filteredEvents.find(e => e.type === 'CHAT_QUERY') || null
  const firstQueryTime = firstQueryEvent ? (firstQueryEvent.timestamp - replayStartTime) / 1000 : null
  const firstQueryMs = firstQueryEvent ? (firstQueryEvent.timestamp - replayStartTime) : null

  const firstRun = filteredEvents.find(e => e.type === 'TERMINAL_RUN')
  const timeToFirstRun = firstRun ? Math.round((firstRun.timestamp - replayStartTime) / 1000) : null
  const lastPass = [...filteredEvents].reverse().find(e => e.type === 'TEST_CASE_RESULT' && e.payload?.passed_count === e.payload?.total_tests && e.payload?.total_tests > 0)
  const timeToCompletion = lastPass ? Math.round((lastPass.timestamp - replayStartTime) / 1000) : null
  const allPassEvent = filteredEvents.find(e => e.type === 'TEST_CASE_RESULT' && e.payload?.passed_count === e.payload?.total_tests && e.payload?.total_tests > 0)
  const runsBeforeAllPass = allPassEvent ? filteredEvents.filter(e => e.type === 'TERMINAL_RUN' && e.timestamp < allPassEvent.timestamp).length : null

  let codeAtFirstQuery = null
  if (firstQueryEvent) {
    const lastCodeBeforeQuery = [...filteredEvents].filter(e => e.type?.startsWith('CODE_') && e.timestamp < firstQueryEvent.timestamp && e.payload?.code != null).pop()
    codeAtFirstQuery = lastCodeBeforeQuery ? lastCodeBeforeQuery.payload.code.length : 0
  }

  let segments = [], visibleSegments = [], beforeSegments = [], afterSegments = [], timelineSegments = []
  if (autoSegmentEvents && filteredEvents.length > 0 && replayDuration > 0) {
    const raw = autoSegmentEvents(filteredEvents, replayStartTime, replayDuration)
    segments = raw.map(s => ({ ...s, behavior: s.suggestedBehavior || s.behavior, thinkingSubcategory: s.suggestedThinkingSubcategory || s.thinkingSubcategory, confidence: s.suggestedBehavior ? 3 : s.confidence }))
    visibleSegments = segments.filter(s => !s.isSystemEvent)
    beforeSegments = firstQueryMs !== null ? visibleSegments.filter(s => s.startTime < firstQueryMs) : visibleSegments
    afterSegments = firstQueryMs !== null ? visibleSegments.filter(s => s.startTime >= firstQueryMs) : []
    timelineSegments = visibleSegments.map(s => ({ segment: s.behavior?.id || 'unknown', start: s.startTime / 1000, end: s.endTime / 1000 }))
  }

  const firstQueryIdx = filteredEvents.findIndex(e => e.type === 'CHAT_QUERY')
  const beforeEvents = firstQueryIdx === -1 ? filteredEvents : filteredEvents.slice(0, firstQueryIdx)
  const afterEventsSlice = firstQueryIdx === -1 ? [] : filteredEvents.slice(firstQueryIdx)
  const behavior = { full: deriveBehavior(sessionStats.full, filteredEvents), 'before-query': deriveBehavior(sessionStats.before, beforeEvents), 'after-query': deriveBehavior(sessionStats.after, afterEventsSlice) }

  const userMessages = conv.filter(m => m.role === 'user')
  const intentCounts = {}
  userMessages.forEach(m => { if (m.intent) intentCounts[m.intent] = (intentCounts[m.intent] || 0) + 1 })

  const testScoreProgression = filteredEvents.filter(e => e.type === 'TEST_CASE_RESULT').map(e => ({ passed: e.payload?.passed_count || 0, total: e.payload?.total_tests || 0, timestamp: e.timestamp, relativeTime: Math.round((e.timestamp - replayStartTime) / 1000) }))

  return {
    filteredEvents, replayStartTime, replayDuration, sessionStats,
    firstQueryEvent, firstQueryTime, firstQueryMs,
    timeToFirstRun, timeToCompletion, runsBeforeAllPass, codeAtFirstQuery,
    autoSegments: segments, visibleSegments, beforeSegments, afterSegments, timelineSegments,
    behavior, beforeStats: sessionStats.before, afterStats: sessionStats.after,
    aiProgressionTimeline: sessionStats.aiProgressionTimeline, testScoreProgression,
    conversation: conv, userMessages, intentCounts, messageCount: conv.length,
  }
}

export default computeSessionStats